import Appointment from "@/components/Appointment/Appointment";
import AppointmentSpecials from "@/components/AppointmentSpecials/AppointmentSpecials";
import InputEmail from "@/components/inputs/InputEmail/InputEmail";
import SectionTitle from "@/components/SectionTitle/SectionTitle";
import { ActionTypes, GetterTypes } from "@/store-vuex/types";
import { Component, Vue } from "vue-property-decorator";
import { ParticipantDTO } from "@/models/ParticipantDTO";
import InputTel from "@/components/inputs/InputTel/InputTel";
import InformationBlock from "../InformationBlock/InformationBlock";
import { McsisConstants } from "@/models/constants/McsisConstants";
import LocalityAutoComplete from "../LocalityAutoComplete/LocalityAutoComplete.vue";
import InputText from "../inputs/InputText/InputText.vue";
import InputDate from "../inputs/InputDate/InputDate.vue";
import { ApplicationDefaultValue } from "@/models/constants/ApplicationDefaultValue";
import { LocalityDTO } from "@/models/LocalityDTO";
import { DoctorDTO } from "@/models/DoctorDTO";
import DoctorAutoComplete from "../DoctorAutoComplete/DoctorAutoComplete.vue";
import InputCheckbox from "../inputs/InputCheckbox/InputCheckbox.vue";
import InputMaskedText from "../inputs/InputMaskedText/InputMaskedText.vue";
import InsuranceAutoComplete from "../InsuranceAutoComplete/InsuranceAutoComplete.vue";
import { InsuranceDTO } from "@/models/InsuranceDTO";
import { isValidSwissAvsNumber } from "@/utils/avs-number";

@Component({
  components: {
    Appointment,
    InputEmail,
    InputTel,
    LocalityAutoComplete,
    DoctorAutoComplete,
    InsuranceAutoComplete,
    InputCheckbox,
    InputText,
    InputMaskedText,
    InputDate,
    SectionTitle,
    AppointmentSpecials,
    InformationBlock
  }
})
export default class AppointmentParticipantForm extends Vue {
  public noDoctorInList: boolean = false;
  public initialDoctor: DoctorDTO | null = null;
  private phoneRegex = new RegExp(McsisConstants.PhoneNumberRegex);
  private internationalPhoneRegex = new RegExp(
    McsisConstants.InternationalPhoneNumberRegex
  );
  private emailRegex = new RegExp(McsisConstants.EmailRegex);

  /**
   * Initialize component data and validators
   */
  protected async mounted(): Promise<void> {
    // Default: checkbox unchecked, dropdown enabled
    this.noDoctorInList = false;
    this.initialDoctor = this.participant.doctor;

    this.checkValidity();
  }

  /**
   * If checked, clears the doctor data
   *
   * @param checked True if the checkbox is checked, false otherwise
   */
  public onNoDoctorInList() {
    this.noDoctorInList = !this.noDoctorInList;

    if (this.noDoctorInList) {
      // Checkbox checked: clear dropdown, enable manual fields
      this.participant.doctor = null;
    } else {
      // Checkbox unchecked: enable dropdown, clear manual fields
      this.participant.doctor = this.initialDoctor;
      this.participant.doctorLastname = null;
      this.participant.doctorFirstname = null;
      this.participant.doctorAddress = null;
      this.participant.doctorLocality = null;
      this.participant.doctorPhone = null;
    }

    this.checkValidity();
  }

  /**
   * Get the ParticipantDTO instance
   */
  public get participant(): ParticipantDTO {
    return this.$store.getters[GetterTypes.participant];
  }

  /**
   * True if the AVS number is fully entered (13 digits) but invalid
   * (wrong prefix or check digit). Not shown while the user is still typing.
   */
  public get avsError(): boolean {
  const avsNumber = this.participant.avsNumber;
  return avsNumber != null
    && avsNumber.replace(/\D/g, "").length === 13
    && !this.checkAvsNumberValidity(avsNumber);
}

  /**
   * Set the phone number value in the store and validate the entered value
   *
   * @param phoneNumber
   */
  public setPhoneNumber(phoneNumber: string | null) {
    if (phoneNumber !== null && phoneNumber !== undefined) {
      this.participant.phoneNumber = phoneNumber;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the email value in the store and validate the entered value
   *
   * @param email
   */
  public setEmail(email: string | null) {
    if (email !== null && email !== undefined) {
      this.participant.email = email;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the first name value in the store and validate the entered value
   *
   * @param firstName
   */
  public setFirstName(firstName: string | null) {
    if (firstName !== null && firstName !== undefined) {
      this.participant.firstName = firstName;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the last name value in the store and validate the entered value
   *
   * @param lastName
   */
  public setLastName(lastName: string | null) {
    if (lastName !== null && lastName !== undefined) {
      this.participant.lastName = lastName;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the avs number value in the store and validate the entered value
   *
   * @param avsNumber
   */
  public setAvsNumber(avsNumber: string | null) {
    if (avsNumber !== null && avsNumber !== undefined) {
      this.participant.avsNumber = avsNumber;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the main address line 1 value in the store and validate the entered value
   *
   * @param mainAddress
   */
  public setMainAddressLine1(mainAddress: string | null) {
    if (mainAddress !== null && mainAddress !== undefined) {
      this.participant.mainAddressLine1 = mainAddress;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the birth date value in the store and validate the entered value
   *
   * @param birthDate
   */
  public setBirthDate(birthDate: string | null) {
    if (birthDate !== null && birthDate !== undefined) {
      this.participant.birthDate = birthDate;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the locality value in the store and validate the entered value
   *
   * @param locality
   */
  public setLocality(locality: LocalityDTO | null) {
    this.participant.locality = locality;
    this.$store.dispatch(ActionTypes.setParticipant, this.participant);

    this.checkValidity();
  }

  /**
   * Set the doctor value in the store and validate the entered value
   *
   * @param doctor
   */
  public setDoctor(doctor: DoctorDTO | null) {
    this.participant.doctor =
        doctor != null &&
        doctor !== undefined &&
        doctor.doctorId !== ApplicationDefaultValue.Number ? doctor : null;
    this.$store.dispatch(ActionTypes.setParticipant, this.participant);

    this.checkValidity();
  }

  /**
   * Set the doctor lastname value in the store and validate the entered value
   *
   * @param doctorLastname
   */
  public setDoctorLastname(doctorLastname: string | null) {
    if (doctorLastname !== null && doctorLastname !== undefined) {
      this.participant.doctorLastname = doctorLastname;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the doctor firstname value in the store and validate the entered value
   *
   * @param doctorFirstname
   */
  public setDoctorFirstname(doctorFirstname: string | null) {
    if (doctorFirstname !== null && doctorFirstname !== undefined) {
      this.participant.doctorFirstname = doctorFirstname;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the doctor address value in the store and validate the entered value
   *
   * @param doctorLocality
   */
  public setDoctorAddress(doctorAddress: string | null) {
    if (doctorAddress !== null && doctorAddress !== undefined) {
      this.participant.doctorAddress = doctorAddress;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the doctor locality value in the store and validate the entered value
   *
   * @param doctorLocality
   */
  public setDoctorLocality(doctorLocality: string | null) {
    if (doctorLocality !== null && doctorLocality !== undefined) {
      this.participant.doctorLocality = doctorLocality;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Set the doctor phone number value in the store and validate the entered value
   *
   * @param doctorPhone
   */
  public setDoctorPhone(doctorPhone: string | null) {
    if (doctorPhone !== null && doctorPhone !== undefined) {
      this.participant.doctorPhone = doctorPhone;
      this.$store.dispatch(ActionTypes.setParticipant, this.participant);
    }

    this.checkValidity();
  }

  /**
   * Validate the LocalityAutoComplete component. The input event is not triggered when you empty the input field from the keyboard
   * Therefore this event is triggered when the value is empty from the AutoComplete component
   *
   * @param value
   */
  public checkLocality(value: boolean) {
    this.$emit("validityChange", value);
  }

  /**
   * Validate the DoctorAutoComplete component. The input event is not triggered when you empty the input field from the keyboard
   * Therefore this event is triggered when the value is empty from the AutoComplete component
   *
   * @param value
   */
  public checkDoctor(value: boolean) {
    this.$emit("validityChange", value);
  }

  /**
   * Validate the InsuranceAutoComplete component. The input event is not triggered when you empty the input field from the keyboard
   * Therefore this event is triggered when the value is empty from the AutoComplete component
   *
   * @param value
   */
  public checkInsurance(value: boolean) {
    this.$emit("validityChange", value);
  }

  /**
   * Set the insurance value in the store and validate the entered value
   *
   * @param insurance
   */
  public setInsurance(insurance: InsuranceDTO | null) {
    this.participant.insurance =
      insurance != null &&
      insurance !== undefined &&
      insurance.personId !== ApplicationDefaultValue.Number ? insurance : null;
    this.$store.dispatch(ActionTypes.setParticipant, this.participant);

    this.checkValidity();
  }

  /**
   * Check the overall form validity so as the user knows when we are ready to send the data to the backend
   */
  public checkValidity(): void {
    const emailValidity = this.checkEmailAddressValidity(
      this.participant.email
    );
    const mobileValidity = this.checkPhoneNumberValidity(
      this.participant.phoneNumber
    );
    const firstNameValidity = this.checkFirstNameValidity(
      this.participant.firstName
    );
    const lastNameValidity = this.checkLastNameValidity(
      this.participant.lastName
    );
    const birthDateValidity = this.checkBirthDateValidity(
      this.participant.birthDate
    );
    const mainAddressValidity = this.checkMainAddressLine1Validity(
      this.participant.mainAddressLine1
    );
    const avsNumberValidity = this.checkAvsNumberValidity(
      this.participant.avsNumber
    );
    const localityValidity = this.checkLocalityValidity(
      this.participant.locality
    );
    const doctorValidity = this.checkDoctorValidity(this.participant);
    const insuranceValidity = this.checkInsuranceValidity(
      this.participant.insurance
    );

    const isFormValid =
      mobileValidity &&
      emailValidity &&
      firstNameValidity &&
      lastNameValidity &&
      birthDateValidity &&
      mainAddressValidity &&
      avsNumberValidity &&
      localityValidity &&
      doctorValidity &&
      insuranceValidity;

    this.$emit("validityChange", isFormValid);
  }

  private checkEmailAddressValidity(emailAddress: string | null): boolean {
    return emailAddress != null && this.emailRegex.test(emailAddress);
  }

  private checkPhoneNumberValidity(phoneNumber: string | null): boolean {
    return (
      phoneNumber != null &&
      (this.internationalPhoneRegex.test(phoneNumber) ||
        this.phoneRegex.test(phoneNumber))
    );
  }

  private checkFirstNameValidity(firstName: string | null): boolean {
    return (
      firstName != null &&
      firstName !== undefined &&
      firstName.trim().length > ApplicationDefaultValue.StringEmptyLength
    );
  }

  private checkLastNameValidity(lastName: string | null): boolean {
    return (
      lastName != null &&
      lastName !== undefined &&
      lastName.trim().length > ApplicationDefaultValue.StringEmptyLength
    );
  }

  private checkBirthDateValidity(birthDate: string | null): boolean {
    return (
      birthDate != null &&
      birthDate !== undefined &&
      birthDate.trim().length > ApplicationDefaultValue.StringEmptyLength
    );
  }

  private checkMainAddressLine1Validity(mainAddress: string | null): boolean {
    return (
      mainAddress != null &&
      mainAddress !== undefined &&
      mainAddress.trim().length > ApplicationDefaultValue.StringEmptyLength
    );
  }

  private checkAvsNumberValidity(avsNumber: string | null): boolean {
    if (
      avsNumber == null ||
      avsNumber === undefined ||
      avsNumber.trim().length === 0
    ) {
      return false;
    }

    return isValidSwissAvsNumber(avsNumber);
  }

  private checkLocalityValidity(locality: LocalityDTO | null): boolean {
    return (
      locality != null &&
      locality !== undefined &&
      locality.locId !== ApplicationDefaultValue.Number
    );
  }

  private checkDoctorValidity(participant: ParticipantDTO): boolean {
    if (!this.noDoctorInList) {
      // Checkbox unchecked: must select doctor from dropdown
      return (
        participant.doctor != null &&
        participant.doctor !== undefined &&
        participant.doctor.doctorId !== ApplicationDefaultValue.Number
      );
    } else {
      // Checkbox checked: must fill all manual doctor fields
      return (
        participant.doctorLastname !== null &&
        participant.doctorLastname !== undefined &&
        participant.doctorLastname.trim().length > ApplicationDefaultValue.StringEmptyLength &&
        participant.doctorFirstname !== null &&
        participant.doctorFirstname !== undefined &&
        participant.doctorFirstname.trim().length > ApplicationDefaultValue.StringEmptyLength &&
        participant.doctorAddress !== null &&
        participant.doctorAddress !== undefined &&
        participant.doctorAddress.trim().length > ApplicationDefaultValue.StringEmptyLength &&
        participant.doctorLocality !== null &&
        participant.doctorLocality !== undefined &&
        participant.doctorLocality.trim().length > ApplicationDefaultValue.StringEmptyLength &&
        participant.doctorPhone !== null &&
        participant.doctorPhone !== undefined &&
        participant.doctorPhone.trim().length > ApplicationDefaultValue.StringEmptyLength
      );
    }
  }

  private checkInsuranceValidity(insurance: InsuranceDTO | null): boolean {
    return (
      insurance != null &&
      insurance !== undefined &&
      insurance.personId !== ApplicationDefaultValue.Number
    );
  }

}
